import React from 'react'
import PropTypes from 'prop-types'
import CloseIcon from '@mui/icons-material/Close'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography
} from '@mui/material'
import ChatMessageBubble from '../../../2talk-chat-report/components/chat-message-bubble'

const sortMessages = messages => [...messages].sort((first, second) => {
  const firstDate = first.createdat ? new Date(first.createdat).getTime() : 0
  const secondDate = second.createdat ? new Date(second.createdat).getTime() : 0

  if (firstDate !== secondDate) return firstDate - secondDate
  return Number(first.id || 0) - Number(second.id || 0)
})

const ConversationMessagesModal = ({
  open,
  chat,
  messages,
  loading,
  error,
  intl,
  onClose
}) => {
  const orderedMessages = sortMessages(messages || [])

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="md"
      scroll="paper"
      aria-labelledby="conversation-messages-modal-title"
      PaperProps={{
        sx: {
          height: { xs: '90vh', md: '82vh' },
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle
        id="conversation-messages-modal-title"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6">
            {intl.formatMessage({ id: 'chat.administration.messages.title' }, { chatid: chat ? chat.id : '' })}
          </Typography>
          {chat && (
            <Typography variant="body2" color="textSecondary" noWrap>
              {chat.customername || chat.sendername || chat.customerphone || chat.senderid || '-'}
            </Typography>
          )}
        </Box>
        <IconButton
          aria-label={intl.formatMessage({ id: 'chat.administration.messages.close' })}
          onClick={onClose}
          disabled={loading}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ backgroundColor: '#F8FAFC', padding: { xs: '14px', sm: '20px' } }}>
        {loading && (
          <Box display="flex" alignItems="center" justifyContent="center" sx={{ minHeight: '240px' }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error">{error}</Alert>
        )}

        {!loading && !error && orderedMessages.length === 0 && (
          <Box display="flex" alignItems="center" justifyContent="center" sx={{ minHeight: '240px' }}>
            <Typography variant="body2" color="textSecondary">
              {intl.formatMessage({ id: 'chat.administration.messages.empty' })}
            </Typography>
          </Box>
        )}

        {!loading && !error && orderedMessages.map(message => (
          <ChatMessageBubble key={message.id} item={message} />
        ))}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading} color="primary">
          {intl.formatMessage({ id: 'chat.administration.messages.close' })}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

ConversationMessagesModal.defaultProps = {
  chat: null,
  messages: [],
  error: ''
}

ConversationMessagesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  chat: PropTypes.object,
  messages: PropTypes.array,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  intl: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired
}

export default ConversationMessagesModal
